import React, { useContext, useMemo, useState, useRef, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { AppContext } from '../../context/AppContext';
import { calcularLastresLiga } from '../../utils/lastreCalculator';
import styled from 'styled-components';
import LastresInstrucciones from './LastresInstrucciones';
import { FaArrowLeft, FaCalculator } from '../../components/Icons';
import { FaInfoCircle, FaChevronDown, FaChevronUp, FaChevronRight } from 'react-icons/fa';
import { Socio } from '../../models/types';
import { theme } from '../../theme';
import Layout from '../../components/Layout';

// Helper function to get initials from a name
const getInitials = (name: string): string => {
  if (!name) return '??';
  return name
    .split(' ')
    .map(part => part[0]?.toUpperCase() || '')
    .filter((_, index, array) => index === 0 || index === array.length - 1)
    .join('')
    .substring(0, 2);
};

// Helper function to generate SVG data URI for initials avatar
const generateInitialsAvatar = (name: string): string => {
  const initials = getInitials(name);
  const canvas = document.createElement('canvas');
  canvas.width = 100;
  canvas.height = 100;
  const context = canvas.getContext('2d');
  
  if (!context) return '';
  
  // Generate a consistent color based on the name
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  const hue = Math.abs(hash % 360);
  const saturation = 70 + (Math.abs(hash) % 20); // 70-90%
  const lightness = 50 + (Math.abs(hash) % 10); // 50-60%
  
  // Draw background
  context.fillStyle = `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  context.fillRect(0, 0, canvas.width, canvas.height);
  
  // Draw text
  context.fillStyle = '#ffffff';
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.font = 'bold 40px Arial';
  context.fillText(initials, canvas.width / 2, canvas.height / 2);
  
  return canvas.toDataURL();
};

// Default avatar SVG as base64
const defaultAvatar = `data:image/svg+xml;base64,${btoa('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="100" height="100"><rect width="100" height="100" fill="#cccccc"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-family="Arial" font-size="40" fill="#555555">?</text></svg>')}`;

// --- Estilos para vista mobile (cards de pilotos) ---
const PilotoCardList = styled.div`
  display: none;
  flex-direction: column;
  gap: 16px;
  @media (max-width: 768px) {
    display: flex;
  }
`;

const PilotoCard = styled.div`
  background: ${theme.colors.background.paper};
  border-radius: ${theme.borderRadius.medium};
  box-shadow: ${theme.shadows.small};
  padding: 14px 10px 12px 10px;
  margin-bottom: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
`;
const PilotoCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
`;
const PilotoCardPos = styled.div`
  font-size: 1.3rem;
  font-weight: ${theme.typography.fontWeights.bold};
  color: ${theme.colors.primary.main};
  min-width: 32px;
  text-align: center;
`;
const PilotoCardInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;
const PilotoCardLastre = styled.div`
  margin: 8px 0 4px 0;
`;
const PilotoCardDetalles = styled.div`
  margin-top: 5px;
`;

// --- FIN estilos mobile ---

// Estilos actualizados para coincidir con el diseño de la aplicación
const PageContainer = styled.div`
  padding: 24px;
  max-width: 1280px;
  margin: 0 auto;
  background-color: ${theme.colors.background.default};
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 200px;
    background: linear-gradient(135deg, ${theme.colors.primary.main} 0%, ${theme.colors.primary.light} 100%);
    z-index: 0;
  }

  @media (max-width: 768px) {
    padding: 8px;
    max-width: 100vw;
    margin: 0;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  position: relative;
  z-index: 1;
  padding-top: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    margin-bottom: 16px;
    padding-top: 8px;
  }
`;

const Title = styled.h1`
  color: white;
  font-weight: ${theme.typography.fontWeights.bold};
  font-size: 2.4rem;
  position: relative;
  padding-bottom: 12px;
  display: flex;
  align-items: center;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 60px;
    height: 4px;
    background-color: ${theme.colors.secondary.main};
    border-radius: 2px;
  }
  
  svg {
    margin-right: 16px;
    color: ${theme.colors.secondary.main};
    font-size: 2rem;
  }

  @media (max-width: 768px) {
    font-size: 1.3rem;
    padding-bottom: 6px;
    svg {
      font-size: 1.2rem;
      margin-right: 8px;
    }
  }
`;

const BackButton = styled(Link)`
  display: flex;
  align-items: center;
  color: white;
  text-decoration: none;
  margin-bottom: 24px;
  font-weight: ${theme.typography.fontWeights.medium};
  transition: all 0.2s;
  position: relative;
  z-index: 1;
  padding: 8px 16px;
  border-radius: ${theme.borderRadius.medium};
  background-color: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(5px);
  
  &:hover {
    color: white;
    background-color: ${theme.colors.secondary.main};
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
  
  svg {
    margin-right: 8px;
  }
`;

const Card = styled.div`
  background: ${theme.colors.background.paper};
  border-radius: ${theme.borderRadius.medium};
  padding: 32px;
  box-shadow: ${theme.shadows.medium};
  margin-bottom: 32px;
  overflow: hidden;
  position: relative;
  z-index: 1;
  border-top: 4px solid ${theme.colors.secondary.main};
  transition: ${theme.transitions.normal};
  
  &:hover {
    box-shadow: ${theme.shadows.large};
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    padding: 16px 8px;
    margin-bottom: 16px;
  }
`;

const SectionHeader = styled.h2`
  background: linear-gradient(135deg, #181c2f 0%, #232746 100%);
  color: white;
  font-size: 2rem;
  font-weight: ${theme.typography.fontWeights.bold};
  margin-top: 0;
  margin-bottom: 24px;
  padding: 24px 32px 18px 32px;
  border-radius: 10px 10px 0 0;
  display: flex;
  align-items: center;
  position: relative;
  box-shadow: 0 2px 8px rgba(16, 19, 37, 0.04);
  border: none;
  text-shadow: 0 2px 4px rgba(0,0,0,0.15);
  z-index: 2;

  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 32px;
    width: 60px;
    height: 4px;
    background-color: ${theme.colors.secondary.main};
    border-radius: 2px;
  }
  
  svg {
    margin-right: 16px;
    color: #e4312e;
    font-size: 2rem;
  }

  @media (max-width: 768px) {
    font-size: 1.1rem;
    padding: 12px 8px 10px 8px;
    margin-bottom: 12px;
  }
`;


interface SectionHeaderProps {
  icon: React.ReactNode;
  children: React.ReactNode;
}

export const SectionHeaderComp: React.FC<SectionHeaderProps> = ({ icon, children }) => (
  <SectionHeader>
    {icon}
    {children}
  </SectionHeader>
);

const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  margin-bottom: 24px;
  border-radius: ${theme.borderRadius.medium};
  box-shadow: ${theme.shadows.small};
  background: linear-gradient(to bottom, rgba(16, 19, 37, 0.02) 0%, rgba(16, 19, 37, 0) 100%);

  @media (max-width: 768px) {
    display: none;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  background-color: ${theme.colors.background.paper};
  border-radius: ${theme.borderRadius.medium};
  overflow: hidden;
  border: 1px solid rgba(16, 19, 37, 0.08);

  @media (max-width: 768px) {
    font-size: 0.92rem;
    min-width: 600px;
  }
`;

const Th = styled.th`
  background: linear-gradient(135deg, ${theme.colors.primary.main} 0%, ${theme.colors.primary.light} 100%);
  color: white;
  text-align: left;
  padding: 18px 16px;
  font-weight: ${theme.typography.fontWeights.semibold};
  position: relative;
  white-space: nowrap;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  font-size: 0.9rem;
  
  &:first-child {
    border-top-left-radius: ${theme.borderRadius.small};
  }
  
  &:last-child {
    border-top-right-radius: ${theme.borderRadius.small};
  }
`;

const Td = styled.td`
  padding: 16px;
  border-bottom: 1px solid ${theme.colors.divider};
  color: ${theme.colors.text.primary};
  vertical-align: middle;
  
  &:first-child {
    font-weight: ${theme.typography.fontWeights.medium};
    font-size: 1.2rem;
    color: ${theme.colors.primary.main};
    text-align: center;
    width: 60px;
  }
`;

const LastreValue = styled.span`
  font-size: 1.5rem;
  font-weight: ${theme.typography.fontWeights.bold};
  color: ${theme.colors.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 100px;
  margin-left: 26px;
  margin-right: 16px;
  padding: 8px 16px;
  background-color: rgba(16, 19, 37, 0.05);
  border-radius: ${theme.borderRadius.medium};
  box-shadow: inset 0 0 0 1px rgba(16, 19, 37, 0.1);
`;

const PilotCell = styled(Td)`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const PilotAvatar = styled.div`
  width: 136px;
  height: 136px;
  border-radius: 50%;
  background-size: cover;
  background-position: center;
  margin-right: 16px;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  border: 4px solid white;
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
`;

const PilotInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const PilotName = styled.div`
  font-weight: ${theme.typography.fontWeights.semibold};
  color: ${theme.colors.text.primary};
  margin-bottom: 4px;
  font-size: 1.1rem;
  cursor: default;
`;

const PilotTeam = styled.div`
  font-size: 0.9rem;
  color: ${theme.colors.text.secondary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  background-color: rgba(16, 19, 37, 0.05);
  padding: 2px 8px;
  border-radius: 12px;
  display: inline-block;
`;

// Declaramos DetalleCard primero para poder usarlo en otros componentes
const DetalleCard = styled.div`
  background-color: rgba(255, 255, 255, 0.5);
  border-radius: ${theme.borderRadius.small};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
  padding: 8px;
  transition: all 0.2s ease;
  border-left: 2px solid ${theme.colors.secondary.main};
  height: 100%;
  display: flex;
  flex-direction: column;
  
  h3 {
    margin: 0 0 4px 0;
    color: ${theme.colors.primary.main};
    font-size: 0.75rem;
    font-weight: ${theme.typography.fontWeights.medium};
    display: flex;
    align-items: center;
    
    svg {
      margin-right: 4px;
      font-size: 0.75rem;
      color: ${theme.colors.secondary.main};
    }
  }
`;

interface TrProps {
  isFirstPilot?: boolean;
}

const Tr = styled.tr<TrProps>`
  transition: all 0.2s;
  position: relative;
  
  &:hover {
    background-color: rgba(16, 19, 37, 0.05);
    transform: translateY(-1px);
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
    z-index: 2;
    
    & + tr.detalles-row {
      background-color: rgba(16, 19, 37, 0.02);
      z-index: 2;
      
      .detalles-container {
        background-color: rgba(16, 19, 37, 0.02);
        padding-bottom: 4px;
      }
      
      ${DetalleCard} {
        background-color: rgba(255, 255, 255, 0.9);
        box-shadow: 0 2px 5px rgba(0, 0, 0, 0.08);
        transform: translateY(-1px);
      }
    }
  }
  
  &:last-child td {
    border-bottom: none;
  }
  
  &.detalles-row {
    background-color: rgba(16, 19, 37, 0.01);
    transform: none;
    box-shadow: none;
    border-bottom: 1px solid ${theme.colors.divider};
    
    &:hover {
      background-color: rgba(16, 19, 37, 0.01);
      transform: none;
      box-shadow: none;
    }
    
    td {
      padding: 0;
    }
    
    &:last-of-type {
      margin-bottom: 12px;
    }
  }
  
  &.piloto-row {
    border-top: 1px solid ${theme.colors.divider};
    
    &.first-pilot {
      border-top: none;
    }
  }
`;

const Badge = styled.span<{ variant?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 0.8rem;
  font-weight: ${theme.typography.fontWeights.medium};
  background-color: ${props => 
    props.variant === 'primary' ? '#dc3545' : /* Rojo para mejor contraste */
    props.variant === 'success' ? theme.colors.success.light :
    props.variant === 'warning' ? theme.colors.warning.light :
    props.variant === 'error' ? theme.colors.error.light :
    theme.colors.secondary.light
  };
  color: white; /* Texto siempre blanco para mejor contraste */
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  text-shadow: 0 1px 1px rgba(0,0,0,0.2);
`;

const InfoBox = styled.div`
  background-color: rgba(16, 19, 37, 0.03);
  border-left: 4px solid ${theme.colors.secondary.main};
  padding: 20px;
  margin-bottom: 24px;
  border-radius: ${theme.borderRadius.small};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  
  p {
    margin: 0 0 8px 0;
    line-height: 1.6;
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  &:before {
    content: '💡';
    display: block;
    font-size: 1.5rem;
    margin-bottom: 10px;
  }
`;

// Componente para mostrar la explicación detallada del cálculo de lastres
const ExplicacionContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const ExplicacionToggle = styled.button`
  background: none;
  border: 1px solid ${theme.colors.divider};
  color: ${theme.colors.primary.main};
  font-size: 0.9rem;
  padding: 8px 12px;
  display: flex;
  align-items: center;
  cursor: pointer;
  border-radius: ${theme.borderRadius.medium};
  transition: all 0.2s;
  font-weight: ${theme.typography.fontWeights.medium};
  
  &:hover {
    background-color: ${theme.colors.primary.light};
    color: white;
    transform: translateY(-1px);
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  }
  
  svg {
    margin-right: 8px;
  }
  
  &:focus {
    outline: none;
  }
`;

const DetalleItem = styled.div`
  margin: 2px 0;
  padding: 4px 6px;
  background-color: rgba(16, 19, 37, 0.01);
  border-left: 2px solid ${theme.colors.secondary.main};
  border-radius: 0 ${theme.borderRadius.small} ${theme.borderRadius.small} 0;
  transition: all 0.2s;
  font-size: 0.75rem;
  display: flex;
  align-items: flex-start;
  line-height: 1.2;
  word-break: break-word;
  hyphens: auto;
  
  &.positivo {
    border-left-color: #28a745;
    &:hover {
      box-shadow: 0 2px 5px rgba(40, 167, 69, 0.1);
    }
  }
  
  &.negativo {
    border-left-color: #dc3545;
    &:hover {
      box-shadow: 0 2px 5px rgba(220, 53, 69, 0.1);
    }
  }
  
  &.neutral {
    border-left-color: #6c757d;
    &:hover {
      box-shadow: 0 2px 5px rgba(108, 117, 125, 0.1);
    }
  }
  
  svg {
    margin-right: 4px;
    margin-top: 2px;
    flex-shrink: 0;
  }
`;

// Contenedor para las cards de detalles
const DetallesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.5fr 1fr;
  gap: 6px;
  margin-top: 6px;
  width: 100%;
  padding: 0 6px 6px 6px;
  transition: all 0.2s ease;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;
    padding: 0;
  }
`;

// Ya declarado arriba

const DetallesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex: 1;
  overflow-y: auto;
  max-height: 80px;
  padding-right: 4px;
  font-size: 0.75rem;
  
  &::-webkit-scrollbar {
    width: 3px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.02);
    border-radius: 10px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${theme.colors.primary.light};
    border-radius: 10px;
  }
`;


interface LastreExplicacionProps {
  piloto: Socio;
  detalles: string[];
  kgTotal?: number;
}

const LastreExplicacion: React.FC<LastreExplicacionProps> = ({ piloto, detalles, kgTotal = 0 }) => {
  // Clasificar los detalles por tipo (positivo, negativo, neutral)
  const detallesProcesados = detalles.map(detalle => {
    let tipo = 'neutral';
    let icono = <FaInfoCircle color="#6c757d" />;
    let textoFormateado = detalle;
    
    // Formatear textos largos para mejorar su presentación
    if (detalle.includes('Reducción')) {
      tipo = 'positivo';
      icono = <FaInfoCircle color="#28a745" />;
      
      // Formatear el texto para que sea más corto
      if (detalle.includes('→')) {
        const partes = detalle.split('→');
        const inicio = partes[0].trim();
        const fin = partes[1].trim();
        textoFormateado = `${inicio.split(':')[0]}: ${inicio.split(':')[1].trim()} → ${fin}`;
      }
    } else if (detalle.includes('Posición')) {
      tipo = 'neutral';
      icono = <FaInfoCircle color="#0f3460" />;
    }
    
    return { texto: textoFormateado, tipo, icono };
  });
  
  const [mostrarDetalles, setMostrarDetalles] = useState(false);
  const explicacionRef = useRef<HTMLDivElement>(null);

  // Agrupar los detalles por categoría
  const detallesPorCategoria = {
    posicion: detallesProcesados.filter(d => d.tipo === 'neutral' && d.texto.includes('Posición')),
    positivos: detallesProcesados.filter(d => d.tipo === 'positivo'),
    otros: detallesProcesados.filter(d => d.tipo === 'neutral' && !d.texto.includes('Posición'))
  };

  return (
    <>
      <DetallesGrid>
        <DetalleCard>
          <h3>
            <FaInfoCircle />
            Posición del piloto
          </h3>
          <DetallesList>
            {detallesPorCategoria.posicion.map((detalle, index) => (
              <DetalleItem key={index} className={detalle.tipo}>
                {detalle.icono}
                {detalle.texto}
              </DetalleItem>
            ))}
          </DetallesList>
        </DetalleCard>
        
        <DetalleCard>
          <h3>
            <FaInfoCircle />
            Cálculo de lastre
          </h3>
          <DetallesList>
            {detallesPorCategoria.positivos.length > 0 ? (
              detallesPorCategoria.positivos.map((detalle, index) => (
                <DetalleItem key={index} className={detalle.tipo}>
                  {detalle.icono}
                  {detalle.texto}
                </DetalleItem>
              ))
            ) : (
              <DetalleItem className="positivo">
                <FaInfoCircle color="#28a745" style={{ marginRight: 4, marginTop: 2, flexShrink: 0 }} />
                No hay reducción por equipo (el equipo no obtuvo bonificación).
              </DetalleItem>
            )}
          </DetallesList>
        </DetalleCard>
        
        <DetalleCard>
          <h3>
            <FaInfoCircle />
            Datos
          </h3>
          <DetallesList>
            {detallesPorCategoria.otros.map((detalle, index) => (
              <DetalleItem key={index} className={detalle.tipo}>
                {detalle.icono}
                {detalle.texto}
              </DetalleItem>
            ))}
            {detallesPorCategoria.otros.length === 0 && (
              <p style={{ padding: '10px', color: theme.colors.text.secondary }}>
                Lastre final: {kgTotal} kg
              </p>
            )}
          </DetallesList>
        </DetalleCard>
      </DetallesGrid>
    </>
  );
};


// Componentes responsive para mostrar/ocultar según el tamaño de pantalla
const DesktopTableContainer = styled(TableContainer)`
  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileCardsContainer = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

// ... otros imports y styled-components ...

const LastresLigaPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { data } = useContext(AppContext);
  const navigate = useNavigate();
  const [detallesAbiertos, setDetallesAbiertos] = useState<{[key: string]: boolean}>({});
  
  // Función para alternar la visualización de detalles para un piloto
  const toggleDetalles = (pilotoId: string) => {
    setDetallesAbiertos(prev => ({
      ...prev,
      [pilotoId]: !prev[pilotoId]
    }));
  };

  const liga = data.ligas.find(l => l.id === id);
  const carreras = data.carreras
    .filter(c => c.ligaId === id && c.publicada) // Solo carreras publicadas
    .sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime());
  const socios = data.socios.filter(s => s.Categoria === liga?.categoria);
  const resultados = data.carreras.flatMap(c => c.resultados);

  // Calcular resultados de equipos por carrera
  const equiposResultados: { [carreraId: string]: { [equipo: string]: number } } = {};
  
  // Obtener el número de pilotos que cuentan para la puntuación del equipo (por defecto 3)
  const pilotosPorEquipo = liga?.configuracionLastres?.configuracionEquipos?.pilotosPorEquipo || 3;
  
  // Primero, calcular los puntos por equipo en cada carrera
  carreras.forEach(carrera => {
    const puntosPorEquipo: { [equipo: string]: { puntos: number, pilotos: { id: string, puntos: number }[] } } = {};
    
    // 1. Agrupar los resultados de la carrera por equipo
    carrera.resultados.forEach(resultado => {
      const piloto = socios.find(s => s.id === resultado.socioId);
      if (piloto && piloto.EQUIPO) {
        if (!puntosPorEquipo[piloto.EQUIPO]) {
          puntosPorEquipo[piloto.EQUIPO] = { puntos: 0, pilotos: [] };
        }
        puntosPorEquipo[piloto.EQUIPO].pilotos.push({
          id: piloto.id,
          puntos: resultado.puntos
        });
      }
    });
    
    // 2. Para cada equipo, ordenar sus pilotos por puntos (de mayor a menor) y sumar solo los N mejores
    const puntosFinalesPorEquipo: { [equipo: string]: number } = {};
    
    Object.entries(puntosPorEquipo).forEach(([equipo, datos]) => {
      // Ordenar pilotos por puntos (de mayor a menor)
      const pilotosOrdenados = [...datos.pilotos].sort((a, b) => b.puntos - a.puntos);
      
      // Tomar solo los mejores N pilotos (o menos si hay menos pilotos)
      const pilotosQueCuentan = pilotosOrdenados.slice(0, pilotosPorEquipo);
      
      // Sumar los puntos de los pilotos que cuentan
      const puntosTotales = pilotosQueCuentan.reduce((sum, piloto) => sum + piloto.puntos, 0);
      
      puntosFinalesPorEquipo[equipo] = puntosTotales;
    });
    
    // 3. Ordenar equipos por puntos y asignar posiciones
    const equiposOrdenados = Object.entries(puntosFinalesPorEquipo)
      .sort(([, puntosA], [, puntosB]) => puntosB - puntosA)
      .map(([equipo], index) => ({ equipo, posicion: index + 1 }));
    
    // 4. Crear mapa de posiciones por equipo para esta carrera
    const posicionesPorEquipo: { [equipo: string]: number } = {};
    equiposOrdenados.forEach(({ equipo, posicion }) => {
      posicionesPorEquipo[equipo] = posicion;
    });
    
    equiposResultados[carrera.id] = posicionesPorEquipo;
  });

  // Agregar depuración de resultados
  console.log('[DEBUG] Resultados antes de calcular lastres:', resultados);
  console.log('[DEBUG] Socios completos:', socios);
  
  const lastres = useMemo(() => {
    if (!liga) return {};
    
    console.log('[DEBUG] === INFORMACIÓN DE DEPURACIÓN ===');
    console.log(`[DEBUG] Liga: ${liga.nombre}`);
    console.log(`[DEBUG] Carreras: ${carreras.length}`);
    console.log(`[DEBUG] Socios: ${socios.length}`);
    console.log(`[DEBUG] Resultados: ${resultados.length}`);
    console.log(`[DEBUG] Equipos Resultados: ${Object.keys(equiposResultados).length}`);
    
    // Mostrar información detallada de los socios
    console.log('[DEBUG] === DETALLE DE SOCIOS ===');
    socios.forEach((s, i) => {
      console.log(`[DEBUG] Socio ${i + 1}:`, {
        id: s.id,
        nombre: s.nombre,
        apellido: s.apellido,
        display_name: s.display_name,
        equipo: s.EQUIPO
      });
    });
    
    // Obtener todas las sanciones de la liga
    const sancionesData = data.sanciones?.filter(s => 
      carreras.some(c => c.id === s.carreraId)
    ) || [];
    
    console.log('[DEBUG] === SANCIONES ENCONTRADAS ===', sancionesData);
    
    // Buscar a José Luis Nuñez en los resultados, normalizando el texto para ignorar acentos
    const normalizeText = (text: string) => 
      text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
      
    let joseLuis = socios.find(s => {
      if (!s.display_name) return false;
      const displayName = normalizeText(s.display_name);
      return (displayName.includes('jose') || displayName.includes('josé')) && 
             (displayName.includes('nunez') || displayName.includes('nuñez'));
    });
    
    // Si aún no lo encuentra, buscar por ID si lo conocemos
    if (!joseLuis) {
      joseLuis = socios.find(s => s.id === 'xMDFdNhjx34wwfPV9u5G'); // ID de José Luis Nuñez
    }
    
    if (joseLuis) {
      console.log('[DEBUG] === JOSÉ LUIS NÚÑEZ ENCONTRADO ===');
      console.log('[DEBUG] Datos completos:', joseLuis);
      
      // Obtener todos los resultados de José Luis en formato plano
      const resultadosJoseLuis = carreras.flatMap(carrera => 
        carrera.resultados
          .filter(r => r.socioId === joseLuis.id)
          .map(r => ({
            ...r,
            carreraNombre: carrera.nombre,
            fecha: carrera.fecha
          }))
      );
      
      console.log(`[DEBUG] Resultados de ${joseLuis.display_name || `${joseLuis.nombre} ${joseLuis.apellido}`}:`, resultadosJoseLuis);
      
      // Obtener sanciones para José Luis
      const sancionesJoseLuis = sancionesData.filter(s => s.socioId === joseLuis.id);
      console.log(`[DEBUG] Sanciones para ${joseLuis.display_name}:`, sancionesJoseLuis);
      
      // Calcular total de puntos de sanción
      const totalPuntosSancion = sancionesJoseLuis.reduce((total, sancion) => {
        const puntos = Math.abs(sancion.puntosPenalizacion || 0);
        console.log(`[DEBUG] Sanción aplicada: ${puntos} puntos en ${sancion.carreraId}`);
        return total + puntos;
      }, 0);
      
      console.log(`[DEBUG] Total puntos de sanción para ${joseLuis.display_name}:`, totalPuntosSancion);
      
      // Mostrar también resultados por carrera para depuración
      carreras.forEach(carrera => {
        const resultadosCarrera = carrera.resultados.filter(r => r.socioId === joseLuis.id);
        if (resultadosCarrera.length > 0) {
          console.log(`[DEBUG] Resultados en ${carrera.nombre}:`, resultadosCarrera);
        }
      });
    } else {
      console.log('[DEBUG] José Luis Nuñez NO encontrado en socios');
      console.log('[DEBUG] Nombres de socios disponibles:', socios.map(s => s.display_name || `${s.nombre} ${s.apellido}`));
    }
    
    // Pasar las sanciones al calculador de lastres
    return calcularLastresLiga({ 
      liga, 
      carreras, 
      socios, 
      resultados,
      equiposResultados,
      sanciones: sancionesData // Pasar las sanciones al calculador
    });
  }, [liga, carreras, socios, resultados]);

  // Ordenar socios primero por lastre (de mayor a menor) y luego por posición
  const sociosOrdenados = [...socios].sort((a, b) => {
    const lastreA = lastres[a.id]?.kgTotal || 0;
    const lastreB = lastres[b.id]?.kgTotal || 0;
    
    // Obtener la posición de cada piloto del detalle del lastre
    const posicionTextoA = lastres[a.id]?.detalle?.find(d => d.startsWith('Posición'));
    const posicionTextoB = lastres[b.id]?.detalle?.find(d => d.startsWith('Posición'));
    
    let posicionA = 999; // Valor por defecto alto
    let posicionB = 999;
    
    if (posicionTextoA) {
      const matchA = posicionTextoA.match(/Posición en el campeonato: (\d+)º/);
      if (matchA && matchA[1]) {
        posicionA = parseInt(matchA[1], 10);
      }
    }
    
    if (posicionTextoB) {
      const matchB = posicionTextoB.match(/Posición en el campeonato: (\d+)º/);
      if (matchB && matchB[1]) {
        posicionB = parseInt(matchB[1], 10);
      }
    }
    
    // Primero ordenar por lastre (de mayor a menor)
    if (lastreB !== lastreA) {
      return lastreB - lastreA;
    }
    
    // Si tienen el mismo lastre, ordenar por posición (de menor a mayor)
    return posicionA - posicionB;
  });

  if (!liga) return <PageContainer><p>Liga no encontrada.</p></PageContainer>;

  const mensajePruebaStyle = {
    color: 'red',
    fontSize: '1.5rem',
    fontWeight: 'bold',
    marginBottom: '16px',
    textAlign: 'center',
    padding: '10px',
    backgroundColor: '#ffebee',
    border: '1px solid #ffcdd2',
    borderRadius: '4px',
    margin: '10px 0'
  } as const;

  return (
    <PageContainer>
      {/* <div style={mensajePruebaStyle}>
        PRUEBA EDUARDO - Última actualización: {new Date().toLocaleTimeString()}
      </div>
      <BackButton to={`/ligas/${id}`}>
        <FaArrowLeft /> Volver a la liga
      </BackButton> */}
      <>
      <div style={{marginTop: 0, marginBottom: 32}}>
  <SectionHeaderComp icon={<FaCalculator style={{ color: '#e4312e' }} />}>
    Lastres para la Próxima Carrera
  </SectionHeaderComp>
</div>

      <Card>
        <InfoBox>
          <p>Esta tabla muestra los lastres asignados a cada piloto para la próxima carrera, calculados en base a los resultados de las carreras anteriores.</p>
          <p>Puedes hacer clic en "Ver explicación detallada" para entender cómo se ha calculado el lastre de cada piloto.</p>
        </InfoBox>
                {/* Vista DESKTOP: Tabla */}
         <DesktopTableContainer>
           <Table>
             <thead>
               <tr>
                 <Th>#</Th>
                 <Th>Piloto</Th>
                 <Th>Lastre</Th>
                 <Th>Detalle</Th>
               </tr>
             </thead>
             <tbody>
               {sociosOrdenados.length > 0 ? (
                 sociosOrdenados.map((socio: Socio, index: number) => {
                  // Extraer la posición real del detalle del lastre
                  const posicionText = lastres[socio.id]?.detalle?.find(d => d.startsWith('Posición'));
                  let posicion = index + 1; // Valor por defecto
                  
                  if (posicionText) {
                      // Extraer el número de posición del texto (ej: "Posición en el campeonato: 2º con 23 puntos")
                      const match = posicionText.match(/Posición en el campeonato: (\d+)º/);
                      if (match && match[1]) {
                        posicion = parseInt(match[1], 10);
                      }
                    }
                    return (
                      <React.Fragment key={socio.id}>
                        <Tr className={`piloto-row ${index === 0 ? 'first-pilot' : ''}`}>
                          <Td>{posicion}</Td>
                          <PilotCell>
                            <PilotAvatar 
                              style={{
                                backgroundImage: socio.fotoUrl || socio.fotoPerfil 
                                  ? `url(${socio.fotoUrl || socio.fotoPerfil})`
                                  : 'none',
                                backgroundColor: !socio.fotoUrl && !socio.fotoPerfil 
                                  ? theme.colors.primary.main
                                  : theme.colors.background.default,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontSize: '2.5rem',
                                fontWeight: 'bold'
                              }}
                              onError={(e: React.SyntheticEvent<HTMLDivElement>) => {
                                const target = e.currentTarget as HTMLDivElement;
                                target.style.backgroundImage = 'none';
                                target.style.backgroundColor = theme.colors.primary.main;
                              }}
                            >
                              {(!socio.fotoUrl && !socio.fotoPerfil) && (
                                <span>{getInitials(socio.display_name || socio.nombre || '')}</span>
                              )}
                            </PilotAvatar>
                            <PilotInfo>
                              <PilotName>{socio.display_name || socio.nombre || 'Sin nombre'}</PilotName>
                              {socio.EQUIPO && <PilotTeam>{socio.EQUIPO}</PilotTeam>}
                            </PilotInfo>
                          </PilotCell>
                          <Td>
                            <LastreValue>
                              {lastres[socio.id]?.kgTotal ?? 0} kg
                            </LastreValue>
                          </Td>
                          <Td></Td>
                        </Tr>
                        <Tr className="detalles-row">
                          <Td colSpan={4} style={{ padding: 0 }}>
                            <div className="detalles-container">
                              <LastreExplicacion 
                                piloto={socio} 
                                detalles={lastres[socio.id]?.detalle || []} 
                                kgTotal={lastres[socio.id]?.kgTotal ?? 0}
                              />
                            </div>
                          </Td>
                        </Tr>
                      </React.Fragment>
                    );
                  })
                ) : (
                  <tr>
                    <Td colSpan={4} style={{ textAlign: 'center', padding: '20px' }}>
                      No hay pilotos con lastre según la configuración actual.
                    </Td>
                  </tr>
                )}
              </tbody>
           </Table>
         </DesktopTableContainer>
         {/* Vista MOBILE: Cards */}
         <MobileCardsContainer>
           <PilotoCardList style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {sociosOrdenados.length > 0 ? (
              sociosOrdenados.map((socio: Socio, index: number) => {
               const posicionText = lastres[socio.id]?.detalle?.find(d => d.startsWith('Posición'));
               let posicion = index + 1;
               if (posicionText) {
                 const match = posicionText.match(/Posición en el campeonato: (\d+)º/);
                 if (match && match[1]) {
                   posicion = parseInt(match[1], 10);
                 }
               }
               return (
                 <PilotoCard key={socio.id}>
                   <PilotoCardHeader>
                     <PilotoCardPos>{posicion}</PilotoCardPos>
                     <PilotAvatar
                       style={{
                         backgroundImage: socio.fotoUrl || socio.fotoPerfil
                           ? `url(${socio.fotoUrl || socio.fotoPerfil})`
                           : 'none',
                         backgroundColor: !socio.fotoUrl && !socio.fotoPerfil
                           ? theme.colors.primary.main
                           : theme.colors.background.default,
                         display: 'flex',
                         alignItems: 'center',
                         justifyContent: 'center',
                         color: 'white',
                         fontSize: '2.2rem',
                         fontWeight: 'bold',
                         minWidth: 72,
                         minHeight: 72,
                         width: 72,
                         height: 72
                       }}
                     >
                       {(!socio.fotoUrl && !socio.fotoPerfil) && (
                         <span>{getInitials(socio.display_name || socio.nombre || '')}</span>
                       )}
                     </PilotAvatar>
                     <PilotoCardInfo>
                       <PilotName>{socio.display_name || socio.nombre || 'Sin nombre'}</PilotName>
                       {socio.EQUIPO && <PilotTeam>{socio.EQUIPO}</PilotTeam>}
                     </PilotoCardInfo>
                   </PilotoCardHeader>
                   <PilotoCardLastre>
                     <LastreValue>{lastres[socio.id]?.kgTotal ?? 0} kg</LastreValue>
                   </PilotoCardLastre>
                   <PilotoCardDetalles>
                     <LastreExplicacion
                       piloto={socio}
                       detalles={lastres[socio.id]?.detalle || []}
                       kgTotal={lastres[socio.id]?.kgTotal ?? 0}
                     />
                   </PilotoCardDetalles>
                 </PilotoCard>
               );
             })
          ) : (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              No hay pilotos con lastre según la configuración actual.
            </div>
          )}
        </PilotoCardList>
      </MobileCardsContainer>
      </Card>
      
      {/* Bloque explicativo detallado sobre el cálculo de lastres */}
      <div style={{marginTop: 48, marginBottom: 32}}>
        <SectionHeaderComp icon={<FaInfoCircle style={{ color: '#e4312e' }} />}>
          Cómo se Calculan los Lastres - Explicación Detallada
        </SectionHeaderComp>
      </div>
      
      <Card style={{ padding: '32px', lineHeight: '1.8', fontSize: '1rem' }}>
        <h3 style={{ color: theme.colors.primary.main, marginBottom: '24px', fontSize: '1.5rem' }}>
          Sistema de Cálculo de Lastres VRSimracers
        </h3>
        
        <div style={{ marginBottom: '32px' }}>
          <h4 style={{ color: theme.colors.secondary.main, marginBottom: '16px', fontSize: '1.3rem' }}>
            📊 1. Cálculo de Puntos Totales por Piloto
          </h4>
          <p style={{ marginBottom: '16px' }}>
            <strong>Puntos base de carrera:</strong> Cada piloto acumula puntos según su rendimiento en cada carrera:
          </p>
          <ul style={{ marginLeft: '20px', marginBottom: '16px' }}>
            <li><strong>Carrera completada:</strong> Puntos según el sistema de puntuación configurado (ej: 25-18-15-12-10-8-6-4-2-1)</li>
            <li><strong>DNF (No termina):</strong> 1 punto automático (para evitar penalización excesiva)</li>
            <li><strong>No participación:</strong> 0 puntos</li>
          </ul>
          
          <p style={{ marginBottom: '16px' }}>
            <strong>Aplicación de sanciones:</strong> Se restan los puntos de penalización de sanciones aplicadas:
          </p>
          <ul style={{ marginLeft: '20px', marginBottom: '16px' }}>
            <li>Sanciones automáticas (por DNF, incidentes, etc.)</li>
            <li>Sanciones manuales por reclamaciones</li>
            <li>Los puntos finales nunca pueden ser negativos (mínimo 0)</li>
          </ul>
        </div>

        <div style={{ marginBottom: '32px' }}>
          <h4 style={{ color: theme.colors.secondary.main, marginBottom: '16px', fontSize: '1.3rem' }}>
            🏆 2. Clasificación General del Campeonato
          </h4>
          <p style={{ marginBottom: '16px' }}>
            Los pilotos se ordenan siguiendo <strong>EXACTAMENTE</strong> estos criterios de desempate:
          </p>
          <ol style={{ marginLeft: '20px', marginBottom: '16px' }}>
            <li><strong>1º criterio:</strong> Mayor número de puntos totales</li>
            <li><strong>2º criterio:</strong> Mayor número de victorias (1ª posición)</li>
            <li><strong>3º criterio:</strong> Mayor número de segundos puestos</li>
            <li><strong>4º criterio:</strong> Mayor número de terceros puestos</li>
          </ol>
          
          <div style={{ backgroundColor: '#fff3cd', padding: '16px', borderRadius: '8px', border: '1px solid #ffeaa7', marginBottom: '16px' }}>
            <strong>⚠️ IMPORTANTE - Gestión de Empates:</strong>
            <p style={{ margin: '8px 0 0 0' }}>
              Si varios pilotos tienen los <strong>mismos puntos totales</strong>, TODOS reciben la <strong>misma posición</strong> 
              y el <strong>mismo lastre</strong>, independientemente de victorias, segundos o terceros puestos. 
              Ejemplo: Si 3 pilotos están empatados a 23 puntos en las posiciones 2º, 3º y 4º, 
              TODOS recibirán la posición 2º y el lastre correspondiente a la 2ª posición.
            </p>
          </div>
        </div>

        <div style={{ marginBottom: '32px' }}>
          <h4 style={{ color: theme.colors.secondary.main, marginBottom: '16px', fontSize: '1.3rem' }}>
            ⚖️ 3. Asignación de Lastres Individuales
          </h4>
          <p style={{ marginBottom: '16px' }}>
            El sistema asigna lastres según la <strong>configuración establecida para la liga</strong>:
          </p>
          
          <div style={{ backgroundColor: '#e8f5e8', padding: '16px', borderRadius: '8px', border: '1px solid #c3e6c3', marginBottom: '16px' }}>
            <strong>📋 Configuración típica de lastres:</strong>
            <ul style={{ marginLeft: '20px', marginTop: '8px', marginBottom: '0' }}>
              <li>1º puesto: X kg</li>
              <li>2º puesto: Y kg</li>
              <li>3º puesto: Z kg</li>
              <li>... (según configuración de la liga)</li>
              <li>A partir de cierta posición: 0 kg</li>
            </ul>
          </div>
          
          <p style={{ marginBottom: '16px' }}>
            <strong>Proceso de asignación:</strong>
          </p>
          <ol style={{ marginLeft: '20px', marginBottom: '16px' }}>
            <li>Se agrupan los pilotos por puntos totales idénticos</li>
            <li>Cada grupo recibe el lastre correspondiente a su posición en la clasificación</li>
            <li>Si hay empates, todos los pilotos empatados reciben el mismo lastre</li>
            <li>Los pilotos fuera del rango configurado reciben 0 kg</li>
          </ol>
        </div>

        <div style={{ marginBottom: '32px' }}>
          <h4 style={{ color: theme.colors.secondary.main, marginBottom: '16px', fontSize: '1.3rem' }}>
            👥 4. Modificaciones por Clasificación de Equipos
          </h4>
          <p style={{ marginBottom: '16px' }}>
            Adicionalmente, se pueden aplicar <strong>reducciones de lastre según la posición del equipo</strong>:
          </p>
          
          <div style={{ backgroundColor: '#e3f2fd', padding: '16px', borderRadius: '8px', border: '1px solid #bbdefb', marginBottom: '16px' }}>
            <strong>🏁 Cálculo de posición de equipos:</strong>
            <ol style={{ marginLeft: '20px', marginTop: '8px', marginBottom: '8px' }}>
              <li>Se suman todos los puntos de los pilotos de cada equipo</li>
              <li>Se ordenan los equipos de mayor a menor puntuación total</li>
              <li>Se aplica la reducción correspondiente según la configuración</li>
            </ol>
            
            <strong>Ejemplo de reducciones:</strong>
            <ul style={{ marginLeft: '20px', marginTop: '8px', marginBottom: '0' }}>
              <li>Equipo 1º: -5 kg</li>
              <li>Equipo 2º: -3 kg</li>
              <li>Equipo 3º: -1 kg</li>
              <li>Resto de equipos: 0 kg</li>
            </ul>
          </div>
          
          <p style={{ marginBottom: '16px' }}>
            <strong>Aplicación final:</strong> Lastre Final = Lastre Individual + Reducción por Equipo (nunca menor a 0 kg)
          </p>
        </div>

        <div style={{ marginBottom: '32px' }}>
          <h4 style={{ color: theme.colors.secondary.main, marginBottom: '16px', fontSize: '1.3rem' }}>
            🔄 5. Actualización Automática del Sistema
          </h4>
          <p style={{ marginBottom: '16px' }}>
            El cálculo de lastres se actualiza automáticamente cuando:
          </p>
          <ul style={{ marginLeft: '20px', marginBottom: '16px' }}>
            <li>Se publican resultados de una nueva carrera</li>
            <li>Se aplican o modifican sanciones</li>
            <li>Se procesan reclamaciones con penalizaciones</li>
            <li>Se cambia la configuración de lastres de la liga</li>
          </ul>
        </div>

        <div style={{ backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '8px', border: '1px solid #dee2e6' }}>
          <h4 style={{ color: theme.colors.primary.main, marginBottom: '16px', fontSize: '1.3rem' }}>
            💡 Resumen del Algoritmo
          </h4>
          <ol style={{ marginLeft: '20px', marginBottom: '0' }}>
            <li><strong>Recopilar:</strong> Solo carreras marcadas como "publicadas"</li>
            <li><strong>Sumar:</strong> Puntos de cada piloto (1 punto por DNF, puntos normales por clasificación)</li>
            <li><strong>Restar:</strong> Puntos de sanciones aplicadas</li>
            <li><strong>Clasificar:</strong> Por puntos totales → victorias → segundos → terceros</li>
            <li><strong>Agrupar:</strong> Pilotos con idénticos puntos totales (misma posición y lastre)</li>
            <li><strong>Asignar:</strong> Lastre según configuración de la liga</li>
            <li><strong>Ajustar:</strong> Aplicar reducciones por posición de equipo</li>
            <li><strong>Validar:</strong> Que el lastre final nunca sea negativo</li>
          </ol>
        </div>
      </Card>
      </>
    </PageContainer>
  );
}

export default LastresLigaPage;
